import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Typography,
  Box,
  Chip,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Search,
  Dashboard,
  AttachMoney,
  AccountTree,
  AutoAwesome,
  Tune,
  Assessment,
  CompareArrows,
  Inventory2,
  AdminPanelSettings,
  Business,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDepartment } from '../contexts/DepartmentContext.jsx';

const NAVIGATION_COMMANDS = [
  { text: 'Dashboard Overview', path: '/', category: 'Navigation', icon: <Dashboard /> },
  { text: 'Department Chargeback & Costs', path: '/costs/chargeback', category: 'Financials', icon: <AttachMoney /> },
  { text: 'Cloud Providers Costs', path: '/costs/providers', category: 'Financials', icon: <AttachMoney /> },
  { text: 'Cloud Services & SaaS Costs', path: '/costs/services', category: 'Financials', icon: <AttachMoney /> },
  { text: 'Cloud Explorer & Drilldown', path: '/explorer', category: 'Investigation', icon: <AccountTree /> },
  { text: 'AI FinOps Insights & Q&A', path: '/ai', category: 'AI & Analytics', icon: <AutoAwesome /> },
  { text: 'Resource Right-Sizing', path: '/rightsizing', category: 'Optimization', icon: <Tune /> },
  { text: 'Cost Forecasts', path: '/forecast', category: 'Optimization', icon: <Assessment /> },
  { text: 'Multi-Period Cost Comparison', path: '/comparison', category: 'Optimization', icon: <CompareArrows /> },
  { text: 'Resource Inventory', path: '/inventory', category: 'Asset Management', icon: <Inventory2 /> },
  { text: 'Admin Console', path: '/admin', category: 'Administration', icon: <AdminPanelSettings /> },
];

export default function CommandPalette() {
  const { commandPaletteOpen, setCommandPaletteOpen, setSelectedDepartment, departments } = useDepartment();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();

  // Combine navigation commands with department filter commands
  const departmentCommands = departments
    .filter((d) => d.id !== 'all')
    .map((d) => ({
      text: `Switch Context: ${d.name}`,
      deptId: d.id,
      category: 'Department Filters',
      icon: <Business />,
    }));

  const allCommands = [...NAVIGATION_COMMANDS, ...departmentCommands];

  const filteredCommands = allCommands.filter((cmd) =>
    cmd.text.toLowerCase().includes(query.toLowerCase()) ||
    cmd.category.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleSelect = (command) => {
    if (command.path) {
      navigate(command.path);
    } else if (command.deptId) {
      setSelectedDepartment(command.deptId);
    }
    setCommandPaletteOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % (filteredCommands.length || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredCommands.length) % (filteredCommands.length || 1));
    } else if (e.key === 'Enter' && filteredCommands[selectedIndex]) {
      e.preventDefault();
      handleSelect(filteredCommands[selectedIndex]);
    }
  };

  return (
    <Dialog
      open={commandPaletteOpen}
      onClose={() => setCommandPaletteOpen(false)}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundImage: 'none',
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[10],
          overflow: 'hidden',
          top: -100,
        },
      }}
    >
      <Box sx={{ p: 2, pb: 1 }}>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          placeholder="Type a command, page name, or department filter..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'primary.main' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Chip label="ESC" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
              </InputAdornment>
            ),
            sx: { borderRadius: 2 },
          }}
        />
      </Box>

      <DialogContent sx={{ p: 0, maxHeight: 400 }}>
        {filteredCommands.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No matching commands or pages found.
            </Typography>
          </Box>
        ) : (
          <List sx={{ pt: 0, pb: 1 }}>
            {filteredCommands.map((cmd, idx) => (
              <React.Fragment key={cmd.text + idx}>
                {idx === 0 || filteredCommands[idx - 1].category !== cmd.category ? (
                  <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                      {cmd.category}
                    </Typography>
                  </Box>
                ) : null}
                <ListItem disablePadding>
                  <ListItemButton
                    selected={idx === selectedIndex}
                    onClick={() => handleSelect(cmd)}
                    sx={{
                      px: 2.5,
                      py: 1,
                      '&.Mui-selected': {
                        bgcolor: theme.palette.primary.main + '18',
                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: idx === selectedIndex ? 'primary.main' : 'text.secondary', minWidth: 38 }}>
                      {cmd.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={cmd.text}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: idx === selectedIndex ? 700 : 500 }}
                    />
                    <ArrowForward sx={{ fontSize: 16, color: 'text.secondary', opacity: idx === selectedIndex ? 1 : 0.4 }} />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <Divider />
      <Box sx={{ px: 2, py: 1, bgcolor: theme.palette.action.hover, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Use <strong>↑</strong> <strong>↓</strong> to navigate, <strong>↵</strong> to select
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Press <strong>Cmd+K</strong> anytime
        </Typography>
      </Box>
    </Dialog>
  );
}
